import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { BellRing, Send, Check, Settings2, MessageSquare } from "lucide-react";

export default function Reminders() {
  const { user } = useAuth();
  const ownerOnly = user?.role !== "owner";
  const [center, setCenter] = useState([]);
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);
  const [cfgOpen, setCfgOpen] = useState(false);

  const load = () => {
    api.get("/reminders/center").then((r) => setCenter(r.data));
    api.get("/reminders/config").then((r) => setConfig(r.data));
    api.get("/reminders/log").then((r) => setLog(r.data));
  };
  useEffect(() => { load(); }, []);

  const send = async (r) => {
    try {
      await api.post("/reminders/send", { id: r.id, invoice_id: r.invoice_id, customer_id: r.customer_id, channel: "internal", message: r.message });
      toast.success(`Reminder queued for ${r.customer_name}`); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveConfig = async () => {
    try { await api.put("/reminders/config", config); toast.success("Reminder settings saved"); setCfgOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><BellRing className="w-7 h-7 text-cyan-500" /> Reminder Center</h1>
          <p className="text-muted-foreground">Internal reminders now, ready for email, SMS &amp; WhatsApp later.</p>
        </div>
        <Button variant="outline" onClick={() => setCfgOpen(true)} data-testid="reminder-settings-btn"><Settings2 className="w-4 h-4 mr-1.5" /> Configure</Button>
      </div>

      <Tabs defaultValue="due">
        <TabsList>
          <TabsTrigger value="due" data-testid="tab-due">Due & Overdue ({center.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Sent Log ({log.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="due" className="mt-4 space-y-3">
          {center.length === 0 && <Card className="p-10 text-center text-muted-foreground">No reminders triggered today based on your schedule 🎉</Card>}
          {center.map((r) => (
            <Card key={r.id} className="p-4" data-testid={`reminder-${r.invoice_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{r.customer_name}</p>
                    <Badge className="bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30">{r.label}</Badge>
                    <span className="mono text-xs text-muted-foreground">{r.invoice_number} · {money(r.remaining_balance)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 bg-secondary/50 rounded-lg p-2.5 flex gap-2"><MessageSquare className="w-4 h-4 shrink-0 mt-0.5" />{r.message}</p>
                </div>
                {r.sent ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30"><Check className="w-3 h-3 mr-1" /> Sent</Badge>
                  : <Button size="sm" onClick={() => send(r)} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid={`send-${r.invoice_id}`}><Send className="w-4 h-4 mr-1.5" /> Send</Button>}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="sent" className="mt-4 space-y-2">
          {log.map((l) => (
            <Card key={l.id} className="p-3.5 flex justify-between items-center">
              <p className="text-sm text-muted-foreground">{l.message}</p>
              <span className="text-xs text-muted-foreground shrink-0 ml-3">{fmtDate(l.created_at)}</span>
            </Card>
          ))}
          {log.length === 0 && <Card className="p-10 text-center text-muted-foreground">No reminders sent yet.</Card>}
        </TabsContent>
      </Tabs>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="reminder-config">
          <DialogHeader><DialogTitle className="font-head">Reminder Settings</DialogTitle></DialogHeader>
          {config && (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div><p className="font-medium text-sm">Reminders Enabled</p></div>
                <Switch checked={config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="cfg-enabled" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Schedule</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {config.offsets.map((o, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-secondary border border-border">
                      {o.type === "before" ? `${o.days}d before` : o.type === "due" ? "On due date" : `${o.days}d overdue`}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Presets: 3 &amp; 1 day before, due date, then 3, 7, 14 &amp; 30 days overdue.</p>
              </div>
              {["before", "due", "after"].map((k) => (
                <div key={k}>
                  <Label className="text-xs capitalize">{k === "before" ? "Before Due Template" : k === "due" ? "Due Date Template" : "Overdue Template"}</Label>
                  <Textarea value={config.templates[k]} data-testid={`tpl-${k}`} disabled={ownerOnly}
                    onChange={(e) => setConfig({ ...config, templates: { ...config.templates, [k]: e.target.value } })} className="text-sm" />
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">Variables: {"{customer} {invoice} {amount} {due_date} {days} {business}"}</p>
              {ownerOnly && <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="tpl-owner-note">Message templates are Owner-only. You can still adjust the schedule and the on/off toggle.</p>}
              <Button onClick={saveConfig} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-config">Save Settings</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
